import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown, PawPrint, Mountain, Waves, Users, Plane, Frame } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { usePageSeo } from "@/lib/seo";
import { media, CATEGORIES } from "@/lib/api";
import { MaskLines, Marquee, Reveal, SectionHead } from "@/components/site/Motion";
import { PhotoImg } from "@/components/site/PhotoImg";
import { PhotoCard } from "@/components/site/PhotoCard";
import { FeaturedVideo, FromBlog, InstagramStrip } from "@/components/site/Sections";
import { Newsletter } from "@/components/site/Footer";
import { VideoModal } from "@/components/site/VideoModal";

const ICONS = { wildlife: PawPrint, landscape: Mountain, underwater: Waves, "people-culture": Users, travel: Plane };

const splitTitle = (s = "") => {
  const w = s.split(" ");
  if (w.length <= 3) return w;
  const target = s.length / 3;
  const lines = [""];
  w.forEach((word) => {
    const cur = lines[lines.length - 1];
    if (cur && cur.length + word.length / 2 > target && lines.length < 3) lines.push(word);
    else lines[lines.length - 1] = cur ? `${cur} ${word}` : word;
  });
  return lines;
};

const Hero = ({ slides }) => {
  const { t, L, settings } = useLang();
  const [i, setI] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-3.5, 3.5]), { stiffness: 60, damping: 20 });
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % slides.length), 7000);
    return () => clearInterval(id);
  }, [slides.length]);
  const cur = slides[i];
  const title = L(settings, "hero_title") || "Stories Through My Lens";
  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#0b0b0b] [perspective:1400px]"
      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); }}>
      <motion.div style={{ y, rotateX: rx, rotateY: ry }} className="absolute inset-[-4%] will-change-transform">
        <AnimatePresence initial={false}>
          {cur && (
            <motion.div key={cur.id} className="absolute inset-0" initial={{ opacity: 0, scale: 1.12 }} animate={{ opacity: 1, scale: 1.02 }} exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1.6 }, scale: { duration: 8, ease: "linear" } }}>
              <PhotoImg photo={cur} eager sizes="100vw" className="h-full w-full" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <motion.div initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 1.3, ease: [0.7, 0, 0.2, 1], delay: 0.1 }} style={{ originY: 0 }} className="absolute inset-0 z-[5] bg-[#0b0b0b]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-transparent to-black/40" />
      <div className="grain absolute inset-0" />
      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center px-5 pt-16 md:px-10">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="kicker mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-[#5d9a35]" />{L(settings, "hero_kicker")}
        </motion.div>
        <MaskLines testid="hero-title" lines={splitTitle(title)} delay={0.7} className="font-display max-w-5xl text-[3.1rem] font-extrabold leading-[0.9] text-white sm:text-7xl lg:text-[6.4rem]" />
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.8 }} className="mt-7 max-w-md text-base text-white/80 md:text-lg">
          {L(settings, "hero_subtitle")}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
          <Link to="/gallery/wildlife" data-testid="hero-explore-button" className="btn-line">{t("explore")} <ArrowRight size={14} /></Link>
        </motion.div>
      </motion.div>
      <div className="absolute inset-x-0 bottom-0 z-10 mx-auto flex max-w-[1600px] items-end justify-between px-5 pb-8 md:px-10">
        <div className="hidden items-center gap-3 font-mono text-xs text-white/70 sm:flex" data-testid="hero-slide-counter">
          {slides.map((s, k) => (
            <button key={s.id} onClick={() => setI(k)} data-testid={`hero-slide-dot-${k}`} className="flex items-center gap-3">
              <span className={k === i ? "text-white" : "text-white/40"}>{String(k + 1).padStart(2, "0")}</span>
              {k < slides.length - 1 && <span className="relative h-px w-12 overflow-hidden bg-white/20">{k === i && <motion.span key={i} initial={{ x: "-100%" }} animate={{ x: "0%" }} transition={{ duration: 7, ease: "linear" }} className="absolute inset-0 bg-white" />}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-white/60 sm:hidden"><ArrowDown size={14} className="animate-bounce" /><span className="kicker !text-[10px]">{t("scroll")}</span></div>
        <div className="text-right">
          <div className="kicker !text-white/80" data-testid="hero-photo-category">{cur ? t(cur.category) : ""}</div>
          <div className="mt-1 font-mono text-[11px] text-white/55">{cur?.location || L(cur, "title")}</div>
        </div>
      </div>
    </section>
  );
};

const Bento = ({ cats, overrides = {} }) => {
  const { t } = useLang();
  const layout = { wildlife: "md:col-span-6 md:row-span-2", landscape: "md:col-span-3", underwater: "md:col-span-3", "people-culture": "md:col-span-3", travel: "md:col-span-3" };
  return (
    <div className="grid grid-cols-1 gap-3 md:auto-rows-[290px] md:grid-cols-12" data-testid="explore-bento">
      {CATEGORIES.map((c, k) => {
        const info = cats.find((x) => x.slug === c);
        const cover = overrides[c]?.urls || info?.cover;
        const Icon = ICONS[c];
        return (
          <Reveal key={c} delay={k * 0.07} className={`${layout[c]} h-[360px] md:h-auto`}>
            <Link to={`/gallery/${c}`} data-testid={`category-card-${c}`} className="group relative block h-full overflow-hidden bg-[#141414]">
              {cover?.medium && <img src={media(cover.medium)} srcSet={`${media(cover.medium)} 1600w, ${media(cover.display)} 3500w`} sizes={c === "wildlife" ? "(max-width:768px) 100vw, 50vw" : "(max-width:768px) 100vw, 25vw"} alt={t(c)} loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.07]" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute inset-0 border border-white/0 transition-colors duration-700 group-hover:border-white/25" />
              <div className="absolute bottom-0 left-0 p-6">
                <Icon className="mb-4 text-white" size={26} strokeWidth={1.4} />
                <div className={`font-display font-bold text-white ${c === "wildlife" ? "text-4xl md:text-5xl" : "text-2xl"}`}>{t(c)}</div>
                <div className="kicker mt-3 flex items-center gap-2 !text-white/80">{info?.count ?? 0} {t("photos")} <ArrowRight size={12} className="transition-transform duration-500 group-hover:translate-x-1.5" /></div>
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  );
};

const Latest = ({ photos, curated }) => {
  const { t } = useLang();
  const [f, setF] = useState("all");
  const list = useMemo(() => (f === "all" ? photos : photos.filter((p) => p.category === f)).slice(0, curated ? 12 : 6), [photos, f, curated]);
  return (
    <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32" data-testid="latest-photos-section">
      <SectionHead title={t("latest")} action={
        <div className="flex flex-wrap gap-2">
          {["all", ...CATEGORIES].map((c) => (
            <button key={c} onClick={() => setF(c)} data-testid={`category-filter-${c}`}
              className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${f === c ? "border-white bg-white text-black" : "border-white/20 text-white/75 hover:border-white/60"}`}>{c === "all" ? t("all") : t(c)}</button>
          ))}
        </div>} />
      <motion.div layout className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {list.map((p, i) => (
            <motion.div layout key={p.id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="aspect-[4/3]">
              <PhotoCard photo={p} list={list} index={i} className="h-full" sizes="(max-width:640px) 100vw, 33vw" />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

const PrintsCTA = ({ photo }) => {
  const { t } = useLang();
  const r = photo?.variants?.medium ? photo.variants.medium.width / photo.variants.medium.height : 1.5;
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#0e0e0e]" data-testid="prints-cta-section">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 py-20 md:px-10 lg:grid-cols-2 lg:py-28">
        <Reveal className="relative aspect-[3/2] overflow-hidden">
          <img src="/rooms/living.jpg" alt="Living room preview" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 border-[6px] border-[#111] bg-[#f4f2ec] p-[2.2%] shadow-[0_25px_45px_rgba(0,0,0,.45)]" style={{ width: r >= 1 ? "36%" : "20%" }}>
            <div style={{ aspectRatio: r }}><PhotoImg photo={photo} sizes="30vw" className="h-full w-full" /></div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="kicker mb-4">{t("prints")}</div>
          <h2 className="font-display text-4xl font-bold leading-[0.95] text-white sm:text-5xl lg:text-6xl">{t("printsTitle")}</h2>
          <p className="mt-6 max-w-md text-white/70">{t("printsLead")}</p>
          <Link to={`/frame-visualizer${photo ? `?photo=${photo.slug}` : ""}`} data-testid="prints-cta-button" className="btn-line mt-10"><Frame size={14} /> {t("tryFrame")} <ArrowRight size={14} /></Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, settings } = useLang();
  usePageSeo("home");
  const { data: photosData } = useFetch("/photos?limit=1000");
  const { data: catsData } = useFetch("/categories");
  const [video, setVideo] = useState(null);
  const photos = Array.isArray(photosData) ? photosData : [];
  const cats = Array.isArray(catsData) ? catsData : [];
  const byId = Object.fromEntries(photos.map((p) => [p.id, p]));
  const pickIds = (ids) => (Array.isArray(ids) ? ids.map((id) => byId[id]).filter(Boolean) : []);
  const chosenHero = pickIds(settings.home_hero_ids);
  const heroes = chosenHero.length ? chosenHero : photos.filter((p) => p.hero).slice(0, 4);
  const slides = heroes.length ? heroes : photos.slice(0, 3);
  const featured = byId[settings.home_prints_photo_id] || photos.find((p) => p.featured && p.category === "wildlife") || photos[0];
  const aerial = byId[settings.home_newsletter_photo_id] || photos.find((p) => p.category === "travel");
  const curatedLatest = pickIds(settings.home_latest_ids);
  const coverOverrides = Object.fromEntries(Object.entries(settings.home_category_covers || {}).map(([c, id]) => [c, byId[id]]).filter(([, p]) => p));
  return (
    <div data-testid="home-page">
      <Hero slides={slides} />
      <Marquee items={CATEGORIES.map((c) => t(c))} />
      <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32">
        <SectionHead testid="explore-heading" kicker="01 — Portfolio" title={t("exploreWorld")} lead={t("exploreLead")} />
        <Bento cats={cats} overrides={coverOverrides} />
      </section>
      <PrintsCTA photo={featured} />
      <Latest photos={curatedLatest.length ? curatedLatest : [...photos].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))} curated={curatedLatest.length > 0} />
      <FeaturedVideo onPlay={setVideo} videoId={settings.home_video_id} />
      <InstagramStrip />
      <FromBlog />
      <Newsletter bg={aerial?.urls?.display ? media(aerial.urls.display) : ""} />
      <AnimatePresence>{video && <VideoModal video={video} onClose={() => setVideo(null)} />}</AnimatePresence>
    </div>
  );
}
